import { UserIcon } from '@app/components/users/UserIcon';
import { ResourceViewPage } from '@app/core/crud/ResourceViewPage';
import { UserWithGroup } from '@app/core/types/users';
import { deleteUser } from '@app/modules/admin/api';
import { capitalizeFirst } from '@mi-front/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { getAppUserDisplayName } from '../../../../../helpers/user';
import { UsersBreadcrumbs } from '../components/UsersBreadcrumbs';
import { useApiUser } from '../hooks/useApiUser';
import { AdminUsersViewTabContent } from './AdminUsersViewTabContent';
import { buildAdminUsersViewTabs } from './useAdminUsersViewTabs';

/**
 * Admin user detail page with header tabs for info, station access, and permissions.
 */
export function AdminUsersViewPage() {
  const { id } = useParams<{ id: string }>();
  const { formatMessage, $t } = useIntl();
  const userQuery = useApiUser(id, {
    contain: 'group',
  });

  return (
    <ResourceViewPage<UserWithGroup>
      query={userQuery}
      dataCy="users-view-screen"
      htmlTitle={(user) =>
        `${capitalizeFirst(
          $t({
            id: 'common.entities.user',
            defaultMessage: 'usuario',
          }),
        )} ${getAppUserDisplayName(user)}`
      }
      header={{
        icon: <UserIcon />,
        title: (user) => getAppUserDisplayName(user),
        breadcrumbs: () => <UsersBreadcrumbs />,
        tabs: (user) => buildAdminUsersViewTabs(user, $t),
      }}
      editConfig={{
        to: (resource) => `/admin/users/${resource.id}/edit`,
      }}
      deleteConfig={{
        onDelete: (resource) => deleteUser(resource.id),
        title: () =>
          formatMessage({
            id: 'admin.users.deleteScreen.title',
            defaultMessage: 'Eliminar usuario',
          }),
        message: (resource) => (
          <FormattedMessage
            id="admin.users.deleteScreen.prompt"
            defaultMessage="¿Desea eliminar el usuario? Esta acción no se puede deshacer."
            values={{
              email: resource.email,
            }}
          />
        ),
        successMessage: () => (
          <FormattedMessage
            id="admin.users.notifications.deleted"
            defaultMessage="Usuario eliminado"
          />
        ),
        redirectTo: '/admin/users',
      }}
    >
      {(user: UserWithGroup) => <AdminUsersViewTabContent user={user} />}
    </ResourceViewPage>
  );
}
